import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
// @ts-expect-error react-cytoscapejs has no official types
import CytoscapeComponent from "react-cytoscapejs";
import { Core, ElementDefinition } from "cytoscape";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Switch from "@mui/material/Switch";
import type { ComparisonResult } from "./PathwayComparison";
import { useTheme } from "../../theme/useTheme";

const PATHWAY_PREFIX = "pathway::";

export type PathwayLayoutMode = "circular" | "force-directed";

interface ComparisonNetworkGraphProps {
  result: ComparisonResult;
}

export const ComparisonNetworkGraph: React.FC<ComparisonNetworkGraphProps> = ({
  result,
}) => {
  const {
    genesPerPathway,
    pathwayLabels,
    comparisonGeneList,
    oneHopEdges,
    sharedGenes,
  } = result;
  const themeCtx = useTheme();
  const cyRef = useRef<Core | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const layoutInitialized = useRef(false);
  const selectedNodeLabelRef = useRef<string | null>(null);

  const [labelFontSize, setLabelFontSize] = useState(9);
  const [pathwayLayout, setPathwayLayout] = useState<PathwayLayoutMode>("force-directed");
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [selectedNodeLabel, setSelectedNodeLabel] = useState<string | null>(null);
  const [connectedLabels, setConnectedLabels] = useState<string[]>([]);
  const [selectedNodeIsPathway, setSelectedNodeIsPathway] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenWrapperRef = useRef<HTMLDivElement>(null);
  /** Cytoscape node ids: tapped node + 1-hop neighborhood for dim/highlight */
  const [graphTapFocusId, setGraphTapFocusId] = useState<string | null>(null);
  const [graphTapNeighborIds, setGraphTapNeighborIds] = useState<string[]>([]);

  useEffect(() => {
    selectedNodeLabelRef.current = selectedNodeLabel;
  }, [selectedNodeLabel]);

  useEffect(() => {
    layoutInitialized.current = false;
    setGraphTapFocusId(null);
    setGraphTapNeighborIds([]);
  }, [result]);

  const elements = useMemo((): ElementDefinition[] => {
    const nodes: ElementDefinition[] = [];
    const edges: ElementDefinition[] = [];
    const geneSet = new Set(comparisonGeneList);

    comparisonGeneList.forEach((gene) => {
      nodes.push({
        data: {
          id: gene,
          label: gene,
          nodeType: sharedGenes.has(gene) ? "gene-shared" : "gene-other",
        },
      });
    });

    Object.keys(genesPerPathway).forEach((pathwayValue) => {
      const pid = PATHWAY_PREFIX + pathwayValue;
      const label = pathwayLabels[pathwayValue] ?? pathwayValue;
      nodes.push({
        data: { id: pid, label, nodeType: "pathway" },
      });
      const genes = genesPerPathway[pathwayValue] ?? [];
      genes.forEach((g) => {
        if (geneSet.has(g)) {
          edges.push({
            data: { source: pid, target: g, edgeType: "pathway-gene" },
          });
        }
      });
    });

    oneHopEdges.forEach((e) => {
      if (geneSet.has(e.source) && geneSet.has(e.target)) {
        edges.push({
          data: { source: e.source, target: e.target, edgeType: "gene-gene" },
        });
      }
    });

    return [...nodes, ...edges];
  }, [
    comparisonGeneList,
    genesPerPathway,
    pathwayLabels,
    oneHopEdges,
    sharedGenes,
  ]);

  const isDark = themeCtx.mode === "dark";
  const labelColor = isDark ? "#e2e8f0" : "#1e293b";
  /** Lighter edge tints aligned with node colors (light / dark) */
  const edgePathwayTint = isDark ? "#93c5fd" : "#bfdbfe";
  const edgeGeneCyanTint = isDark ? "#5eead4" : "#a5f3fc";
  const edgeGeneAmberTint = isDark ? "#fcd34d" : "#fde68a";
  const tapFocusBorderCenter = isDark ? "#f8fafc" : "#0f172a";
  const tapFocusBorderNeighbor = isDark ? "#94a3b8" : "#475569";

  const inGraphTapNeighborhood = (nodeId: string) => {
    if (!graphTapFocusId) return true;
    return nodeId === graphTapFocusId || graphTapNeighborIds.includes(nodeId);
  };
  const isGraphTapCenter = (nodeId: string) => graphTapFocusId != null && nodeId === graphTapFocusId;
  const edgeInGraphTapNeighborhood = (ele: {
    source: () => { id: () => string };
    target: () => { id: () => string };
  }) => {
    if (!graphTapFocusId) return false;
    const s = ele.source().id();
    const t = ele.target().id();
    return inGraphTapNeighborhood(s) && inGraphTapNeighborhood(t);
  };

  const stylesheet = useMemo(
    () => [
      {
        selector: "node",
        style: {
          label: "data(label)",
          "text-halign": "center",
          "font-family": "system-ui, -apple-system, sans-serif",
          "font-weight": 400,
          "text-wrap": "wrap",
          color: labelColor,
          opacity: (ele: { id: () => string }) => {
            if (!graphTapFocusId) return 1;
            return inGraphTapNeighborhood(ele.id()) ? 1 : 0.22;
          },
        } as Record<string, unknown>,
      },
      {
        selector: "node[nodeType='pathway']",
        style: {
          shape: "roundrectangle",
          width: (ele: { id: () => string }) => {
            const base = 52;
            if (!graphTapFocusId) return base;
            return isGraphTapCenter(ele.id()) ? 56 : base;
          },
          height: (ele: { id: () => string }) => {
            const base = 22;
            if (!graphTapFocusId) return base;
            return isGraphTapCenter(ele.id()) ? 26 : base;
          },
          "background-color": isDark ? "#4f46e5" : "#6366f1",
          "border-width": (ele: { id: () => string }) => {
            if (!graphTapFocusId) return 0;
            if (!inGraphTapNeighborhood(ele.id())) return 0;
            return 1;
          },
          "border-color": (ele: { id: () => string }) => {
            if (!graphTapFocusId) return isDark ? "#4f46e5" : "#6366f1";
            if (isGraphTapCenter(ele.id())) return tapFocusBorderCenter;
            if (inGraphTapNeighborhood(ele.id())) return tapFocusBorderNeighbor;
            return isDark ? "#4f46e5" : "#6366f1";
          },
          color: labelColor,
          "text-valign": "top",
          "text-halign": "center",
          "text-margin-y": 0,
          "font-size": `${Math.max(7, Math.min(11, labelFontSize))}px`,
          "font-weight": 400,
          "text-max-width": "140px",
        } as Record<string, unknown>,
      },
      {
        selector: "node[nodeType='gene-shared']",
        style: {
          shape: "ellipse",
          width: (ele: { id: () => string }) => {
            const base = 18;
            if (!graphTapFocusId) return base;
            return isGraphTapCenter(ele.id()) ? 20 : inGraphTapNeighborhood(ele.id()) ? 18 : base;
          },
          height: (ele: { id: () => string }) => {
            const base = 18;
            if (!graphTapFocusId) return base;
            return isGraphTapCenter(ele.id()) ? 20 : inGraphTapNeighborhood(ele.id()) ? 18 : base;
          },
          "background-color": isDark ? "#22d3ee" : "#06b6d4",
          "border-width": (ele: { id: () => string }) => {
            if (!graphTapFocusId) return 0;
            if (!inGraphTapNeighborhood(ele.id())) return 0;
            return 1;
          },
          "border-color": (ele: { id: () => string }) => {
            if (!graphTapFocusId) return isDark ? "#22d3ee" : "#06b6d4";
            if (isGraphTapCenter(ele.id())) return tapFocusBorderCenter;
            if (inGraphTapNeighborhood(ele.id())) return tapFocusBorderNeighbor;
            return isDark ? "#22d3ee" : "#06b6d4";
          },
          label: "data(label)",
          "text-valign": "top",
          "text-margin-y": 0,
          "font-size": `${Math.max(6, labelFontSize)}px`,
          "text-max-width": "80px",
          color: labelColor,
        } as Record<string, unknown>,
      },
      {
        selector: "node[nodeType='gene-other']",
        style: {
          shape: "ellipse",
          width: (ele: { id: () => string }) => {
            const base = 16;
            if (!graphTapFocusId) return base;
            return isGraphTapCenter(ele.id()) ? 20 : inGraphTapNeighborhood(ele.id()) ? 18 : base;
          },
          height: (ele: { id: () => string }) => {
            const base = 16;
            if (!graphTapFocusId) return base;
            return isGraphTapCenter(ele.id()) ? 20 : inGraphTapNeighborhood(ele.id()) ? 18 : base;
          },
          "background-color": isDark ? "#fde047" : "#facc15",
          "border-width": (ele: { id: () => string }) => {
            if (!graphTapFocusId) return 0;
            if (!inGraphTapNeighborhood(ele.id())) return 0;
            return 1;
          },
          "border-color": (ele: { id: () => string }) => {
            if (!graphTapFocusId) return isDark ? "#fde047" : "#facc15";
            if (isGraphTapCenter(ele.id())) return tapFocusBorderCenter;
            if (inGraphTapNeighborhood(ele.id())) return tapFocusBorderNeighbor;
            return isDark ? "#fde047" : "#facc15";
          },
          label: "data(label)",
          "text-valign": "top",
          "text-margin-y": 0,
          "font-size": `${Math.max(6, labelFontSize)}px`,
          "text-max-width": "80px",
          color: labelColor,
        } as Record<string, unknown>,
      },
      {
        selector: "node:active",
        style: {
          "overlay-color": isDark ? "#64748b" : "#94a3b8",
          "overlay-padding": 8,
          "overlay-opacity": 0.2,
        } as Record<string, unknown>,
      },
      {
        selector: "node.dragging",
        style: {
          "border-width": 0,
          "overlay-opacity": 0.25,
          "overlay-padding": 6,
          "overlay-color": isDark ? "#60a5fa" : "#3b82f6",
        } as Record<string, unknown>,
      },
      {
        selector: "edge[edgeType='pathway-gene']",
        style: {
          "line-style": "dashed",
          "target-arrow-shape": "none",
          width: (ele: any) => {
            if (!graphTapFocusId) return 1;
            return edgeInGraphTapNeighborhood(ele) ? 2 : 0.5;
          },
          "line-color": (ele: any) => {
            if (!graphTapFocusId) return edgePathwayTint;
            return edgeInGraphTapNeighborhood(ele) ? edgePathwayTint : isDark ? "#475569" : "#94a3b8";
          },
          "line-opacity": (ele: any) => {
            if (!graphTapFocusId) return 0.85;
            return edgeInGraphTapNeighborhood(ele) ? 1 : 0.12;
          },
          "curve-style": "straight",
        } as Record<string, unknown>,
      },
      {
        selector: "edge[edgeType='gene-gene']",
        style: {
          "line-style": "solid",
          "target-arrow-shape": "none",
          width: (ele: any) => {
            if (!graphTapFocusId) return 1;
            return edgeInGraphTapNeighborhood(ele) ? 2.4 : 0.5;
          },
          "line-color": (ele: any) => {
            if (!graphTapFocusId) {
              const s = ele.source().data("nodeType");
              const t = ele.target().data("nodeType");
              const sharedEnds = s === "gene-shared" || t === "gene-shared";
              return sharedEnds ? edgeGeneCyanTint : edgeGeneAmberTint;
            }
            return edgeInGraphTapNeighborhood(ele) ? edgeGeneCyanTint : isDark ? "#475569" : "#94a3b8";
          },
          "line-opacity": (ele: any) => {
            if (!graphTapFocusId) return 0.75;
            return edgeInGraphTapNeighborhood(ele) ? 1 : 0.12;
          },
          "curve-style": "straight",
        } as Record<string, unknown>,
      },
    ],
    [
      isDark,
      labelFontSize,
      edgePathwayTint,
      edgeGeneCyanTint,
      edgeGeneAmberTint,
      labelColor,
      graphTapFocusId,
      graphTapNeighborIds,
      tapFocusBorderCenter,
      tapFocusBorderNeighbor,
    ]
  );

  const runLayout = useCallback(
    (cy: Core, mode?: PathwayLayoutMode) => {
      const layoutMode = mode ?? pathwayLayout;
      if (layoutMode === "circular") {
        const geneNodes = cy.nodes().filter((n) => {
          const t = n.data("nodeType");
          return t === "gene-shared" || t === "gene-other";
        });
        const pathwayNodes = cy.nodes('[nodeType="pathway"]');
        const geneEdges = cy.edges('[edgeType="gene-gene"]');
        const geneSubgraph = geneNodes.union(geneEdges);

        if (geneNodes.length === 0) {
          const fallback = cy.layout({
            name: "circle",
            animate: true,
            animationDuration: 400,
          } as any);
          fallback.run();
          fallback.on("layoutstop", () => {
            cy.fit(undefined, 30);
            cy.center();
          });
          return;
        }

        const innerLayout = geneSubgraph.layout({
          name: "cose",
          animate: true,
          animationDuration: 700,
          nodeDimensionsIncludeLabels: true,
          idealEdgeLength: 45,
          gravity: 1.15,
          gravityRange: 2.2,
        } as any);

        innerLayout.on("layoutstop", () => {
          const nPath = pathwayNodes.length;
          if (nPath > 0) {
            const bb = geneNodes.boundingBox({ includeLabels: true });
            const cx = (bb.x1 + bb.x2) / 2;
            const cy = (bb.y1 + bb.y2) / 2;
            const half = Math.max(bb.w, bb.h) / 2;
            const r =
              half +
              Math.max(140, half * 0.45, Math.min(220, 80 + nPath * 28));

            /** Place pathways on left & right arcs only (more horizontal space, not top/bottom). */
            const leftCount = Math.ceil(nPath / 2);
            const rightCount = nPath - leftCount;
            const leftArcStart = (2 * Math.PI) / 3;
            const leftArcSpan = (2 * Math.PI) / 3;
            const rightArcStart = -Math.PI / 3;
            const rightArcSpan = (2 * Math.PI) / 3;

            pathwayNodes.forEach((node, i) => {
              let angle: number;
              if (i < leftCount) {
                const k = leftCount === 1 ? 0.5 : i / (leftCount - 1);
                angle = leftArcStart + k * leftArcSpan;
              } else {
                const j = i - leftCount;
                const k = rightCount === 1 ? 0.5 : j / (rightCount - 1);
                angle = rightArcStart + k * rightArcSpan;
              }
              node.position({
                x: cx + r * Math.cos(angle),
                y: cy + r * Math.sin(angle),
              });
            });
          }
          cy.fit(undefined, 30);
          cy.center();
        });
        innerLayout.run();
        return;
      }

      const layout = cy.layout({
        name: "cose",
        animate: true,
        animationDuration: 800,
        nodeDimensionsIncludeLabels: true,
        idealEdgeLength: 60,
        gravity: 1,
        gravityRange: 2.5,
      } as any);
      layout.run();
      layout.on("layoutstop", () => {
        cy.fit(undefined, 30);
        cy.center();
      });
    },
    [pathwayLayout],
  );

  useEffect(() => {
    const onFullscreenChange = () => {
      const fullscreen = document.fullscreenElement === fullscreenWrapperRef.current;
      setIsFullscreen(fullscreen);
      requestAnimationFrame(() => {
        setTimeout(() => {
          const cy = cyRef.current;
          if (cy) {
            cy.resize();
            cy.fit(undefined, 30);
          }
          if (!fullscreen) fullscreenWrapperRef.current?.focus();
        }, 100);
      });
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const cy = cyRef.current;
          if (cy) {
            cy.resize();
            cy.fit(undefined, 30);
          }
        }, 50);
      });
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const cy = cyRef.current;
      if (cy) {
        cy.resize();
        cy.fit(undefined, 30);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [isDrawerOpen]);

  const handleReset = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    layoutInitialized.current = false;
    runLayout(cy);
  }, [runLayout]);

  const handleFullscreen = useCallback(() => {
    const el = fullscreenWrapperRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleDownload = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const png = cy.png({
      output: "blob",
      scale: 2,
      bg: isDark ? "#1e293b" : "#f8fafc",
    });
    const url = URL.createObjectURL(png);
    const link = document.createElement("a");
    link.href = url;
    link.download = "comparison-network.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [cyRef, isDark]);

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/95 p-3 md:p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Comparison Network Visualization
        </h3>
        <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300 mb-3">
          Network visualization showing gene interactions across the selected pathways. Genes that
          are part of multiple pathways are highlighted in cyan. Pathway nodes (blue rectangles)
          are positioned around the gene network and connected to their respective genes.
        </p>
        <div className="flex items-center gap-4 mb-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: isDark ? "#22d3ee" : "#06b6d4" }}
            />
            Shared genes
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: isDark ? "#fde047" : "#facc15" }}
            />
            Other genes
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
            <span
              className="w-4 h-3 rounded shrink-0"
              style={{ backgroundColor: isDark ? "#4f46e5" : "#6366f1" }}
            />
            Pathways
          </span>
        </div>
      </div>

      <div
        ref={fullscreenWrapperRef}
        tabIndex={-1}
        aria-label="Pathway Comparison Network"
        className={`relative z-10 rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden bg-slate-50 dark:bg-slate-800/95 box-border min-w-0 outline-none ${isFullscreen ? "bg-white dark:bg-slate-900" : ""}`}
        style={{
          width: isFullscreen ? "100vw" : "100%",
          maxWidth: isFullscreen ? "none" : "100%",
          height: isFullscreen ? "100vh" : 540,
          minHeight: isFullscreen ? "100vh" : 540,
        }}
      >
        <div
          ref={containerRef}
          className="absolute inset-0 top-0 left-0 transition-all duration-300 min-w-0"
          style={{
            right: isDrawerOpen ? "320px" : "0",
            bottom: 0,
            width: isDrawerOpen ? "calc(100% - 320px)" : "100%",
            maxWidth: "100%",
          }}
        >
          {/* Canvas first so popups/toolbar render above it (canvas would otherwise cover earlier siblings) */}
          <CytoscapeComponent
            elements={elements}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              zIndex: 0,
            }}
            stylesheet={stylesheet}
            cy={(cy: Core) => {
              cyRef.current = cy;
              cy.nodes().ungrabify();
              cy.nodes().grabify();

              cy.on("tap", (evt: any) => {
                if (evt.target === cy) {
                  setGraphTapFocusId(null);
                  setGraphTapNeighborIds([]);
                  setSelectedNodeLabel(null);
                  setConnectedLabels([]);
                }
              });

              cy.on("tap", "node", (evt: any) => {
                const node = evt.target;
                const id = node.data("id") as string;
                const label = node.data("label");
                const nodeType = node.data("nodeType");

                if (selectedNodeLabelRef.current === label) {
                  setSelectedNodeLabel(null);
                  setConnectedLabels([]);
                  setGraphTapFocusId(null);
                  setGraphTapNeighborIds([]);
                  return;
                }

                const neighborNodes = node.neighborhood("node");
                const neighborIds = neighborNodes.map((n: any) => n.id() as string);
                setGraphTapFocusId(id);
                setGraphTapNeighborIds(neighborIds);

                if (nodeType === "pathway") {
                  const pathwayValue = id.startsWith(PATHWAY_PREFIX)
                    ? id.slice(PATHWAY_PREFIX.length)
                    : id;
                  const genes = genesPerPathway[pathwayValue] ?? [];
                  setSelectedNodeLabel(label);
                  setConnectedLabels(genes);
                  setSelectedNodeIsPathway(true);
                  return;
                }

                const labels = neighborNodes
                  .filter((n: any) => n.data("nodeType") !== "pathway")
                  .map((n: any) => n.data("label"))
                  .filter(Boolean);
                setSelectedNodeLabel(label);
                setConnectedLabels(labels);
                setSelectedNodeIsPathway(false);
              });

              cy.on("grab", "node", (evt: any) => {
                evt.target.addClass("dragging");
              });
              cy.on("free", "node", (evt: any) => {
                evt.target.removeClass("dragging");
              });

              if (!layoutInitialized.current) {
                layoutInitialized.current = true;
                runLayout(cy);
              }
            }}
            wheelSensitivity={0.2}
            autoungrabify={false}
            autounselectify={false}
          />
          <div className="absolute top-2 left-2 z-30 flex flex-col gap-1 pointer-events-none">
            <div
              className="bg-black bg-opacity-70 text-white text-xs font-medium px-2 py-1.5 rounded max-w-[min(320px,70vw)] truncate pointer-events-none"
              title="Pathway Comparison Network"
            >
              Pathway Comparison Network
            </div>
            {selectedNodeLabel != null && (
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-w-[min(320px,70vw)] overflow-hidden pointer-events-auto">
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 dark:border-slate-700">
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {selectedNodeIsPathway ? "Pathway: " : "Gene: "}
                    {selectedNodeLabel}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedNodeLabel(null);
                      setConnectedLabels([]);
                      setGraphTapFocusId(null);
                      setGraphTapNeighborIds([]);
                    }}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-500 dark:text-gray-400"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="px-3 py-2 max-h-40 overflow-y-auto">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    {selectedNodeIsPathway ? "Genes in pathway" : "Connected genes"} (
                    {connectedLabels.length}):
                  </p>
                  {connectedLabels.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">None</p>
                  ) : (
                    <p className="text-xs text-gray-700 dark:text-gray-200 break-words">
                      {connectedLabels.join(", ")}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="absolute top-2 right-2 z-30 flex items-center gap-1 pointer-events-auto">
            <button
              onClick={handleReset}
              title="Reset layout"
              className="p-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </button>
            <button
              onClick={handleDownload}
              title="Download as PNG"
              className="p-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </button>
            <button
              onClick={handleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              className="p-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
              ) : (
                <ArrowsPointingOutIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsDrawerOpen(!isDrawerOpen)}
          className="absolute top-1/2 -translate-y-1/2 z-40 flex items-center justify-center w-7 h-16 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 group rounded-l-md"
          style={{ right: isDrawerOpen ? "320px" : "0" }}
          title={isDrawerOpen ? "Hide controls" : "Show controls"}
        >
          {isDrawerOpen ? (
            <ChevronUpIcon className="h-4 w-4 transform rotate-90" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 transform rotate-90" />
          )}
        </button>

        <div
          className={`absolute top-0 h-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 border-l shadow-2xl transition-all duration-300 ease-in-out z-20 overflow-hidden ${isDrawerOpen ? "border-l-0 rounded-r-lg" : "rounded-lg"}`}
          style={{
            width: "320px",
            right: isDrawerOpen ? "0" : "-320px",
            opacity: isDrawerOpen ? 1 : 0,
            pointerEvents: isDrawerOpen ? "auto" : "none",
          }}
        >
          <div className="relative flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 z-20">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm">
                <InformationCircleIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                  Plot Controls
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Adjust visualization settings
                </p>
              </div>
            </div>
          </div>
          <div
            className="relative p-5 space-y-6 overflow-y-auto bg-gray-50 dark:bg-slate-800/50 z-10"
            style={{ height: "calc(100% - 73px)" }}
          >
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Pathway Layout
                </label>
                <div className="group relative">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help" />
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2">
                <span
                  className={`text-sm font-medium shrink-0 min-w-[4.5rem] ${pathwayLayout === "circular" ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"}`}
                >
                  Circular
                </span>
                <Switch
                  checked={pathwayLayout === "force-directed"}
                  onChange={(_, checked) => {
                    const next = checked ? "force-directed" : "circular";
                    setPathwayLayout(next);
                    const cy = cyRef.current;
                    // Pass `next` so layout runs with the new mode (state is async; stale runLayout would use old mode).
                    if (cy) runLayout(cy, next);
                  }}
                  color="primary"
                  size="medium"
                  inputProps={{
                    "aria-label": "Pathway layout: off Circular, on Force-Directed",
                  }}
                />
                <span
                  className={`text-sm font-medium shrink-0 min-w-[6.5rem] text-right ${pathwayLayout === "force-directed" ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"}`}
                >
                  Force-Directed
                </span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Label Font Size
                </label>
                <div className="group relative">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help" />
                </div>
              </div>
              <div className="flex items-center bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setLabelFontSize((s) => Math.max(6, s - 1))}
                  className="flex-1 py-2 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors flex items-center justify-center border-r border-gray-200 dark:border-slate-600"
                >
                  <span className="text-gray-600 dark:text-gray-300 font-bold text-lg">−</span>
                </button>
                <div className="flex-1 py-2 text-center bg-gray-50 dark:bg-slate-700/50">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {labelFontSize}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setLabelFontSize((s) => Math.min(24, s + 1))}
                  className="flex-1 py-2 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors flex items-center justify-center border-l border-gray-200 dark:border-slate-600"
                >
                  <span className="text-gray-600 dark:text-gray-300 font-bold text-lg">+</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
